import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faCartShopping, faQrcode, faArrowPointer } from '@fortawesome/free-solid-svg-icons';
import { ItemDetailsOverview, ItemDetailsResponse, MailTrackingSummary } from '../types';
import styles from '../item-details.module.scss';
import moment from 'moment';

interface StatusSegment {
  color: string;
  count: number;
  label: string;
}

interface StatusGridItem {
  key: keyof MailTrackingSummary;
  label: string;
  color: string;
}

const STATUS_GRID_ITEMS: StatusGridItem[] = [
  { key: 'outForDelivery', label: 'Out for Delivery', color: '#249af3' },
  { key: 'inDestinationState', label: 'In Destination State', color: '#5bb3f6' },
  { key: 'inMailstream', label: 'In Mailstream', color: '#91cdf9' },
  { key: 'inTransit', label: 'In Transit', color: '#c8e6fc' },
  { key: 'relabeled', label: 'Relabeled', color: '#fccece' },
  { key: 'markedForReturn', label: 'Marked for Return', color: '#f78484' },
  { key: 'cancelledByUSPS', label: 'Cancelled by USPS', color: '#f33a3a' },
  { key: 'inProcessing', label: 'In Processing', color: '#ebeced' },
];

const StatusBar: React.FC<{ summary: MailTrackingSummary; total: number }> = ({ summary, total }) => {
  const safeTotal = total || 1;
  const segments: StatusSegment[] = [
    { color: '#249af3', count: summary.outForDelivery, label: 'Out for Delivery' },
    { color: '#5bb3f6', count: summary.inDestinationState, label: 'In Destination State' },
    { color: '#91cdf9', count: summary.inMailstream, label: 'In Mailstream' },
    { color: '#c8e6fc', count: summary.inTransit, label: 'In Transit' },
    { color: '#16d761', count: summary.delivered, label: 'Delivered' },
    { color: '#ebeced', count: summary.inProcessing, label: 'In Processing' },
    { color: '#fccece', count: summary.relabeled, label: 'Relabeled' },
    { color: '#f33a3a', count: summary.markedForReturn + summary.cancelledByUSPS, label: 'Return/Cancelled' },
  ].filter(s => s.count > 0);

  const filledCount = segments.reduce((sum, s) => sum + s.count, 0);
  const remaining = Math.max(0, safeTotal - filledCount);

  return (
    <div className={styles.statusBar}>
      {segments.map((seg, i) => (
        <Tooltip key={i} title={`${seg.label}: ${seg.count}`}>
          <div
            className={styles.statusBarSegment}
            style={{ width: `${(seg.count / safeTotal) * 100}%` }}
          />
        </Tooltip>
      ))}
      {remaining > 0 && (
        <div
          className={styles.statusBarSegment}
          style={{ flex: 1 }}
        />
      )}
    </div>
  );
};

const COMPLETED_STATUSES = new Set(['fulfillment complete', 'shipped', 'delivered']);
const ERROR_STATUSES = new Set(['failed', 'vendor error']);

// Maps the specific order status to the friendly bucket shown in the Order List. Anything not
// explicitly Completed/Cancelled/Error falls back to Processing, per the agreed mapping (PROJ-41527).
const getFriendlyStatus = (status: string): 'Processing' | 'Completed' | 'Cancelled' | 'Error' => {
  const s = status?.toLowerCase();
  if (s === 'cancelled') return 'Cancelled';
  if (ERROR_STATUSES.has(s)) return 'Error';
  if (COMPLETED_STATUSES.has(s)) return 'Completed';
  return 'Processing';
};

const getStatusBadgeClass = (friendlyStatus: string) => {
  if (friendlyStatus === 'Processing') return styles.statusBadgeProcessing;
  if (friendlyStatus === 'Error' || friendlyStatus === 'Cancelled') return styles.statusBadgeError;
  return styles.statusBadgeSuccess;
};

interface MetaRowProps {
  label: string;
  value: React.ReactNode;
  tooltip?: string;
  noDivider?: boolean;
}

const MetaRow: React.FC<MetaRowProps> = ({ label, value, tooltip, noDivider }) => (
  <div className={`${styles.metaRow} ${noDivider ? styles.metaRowNoDivider : ''}`}>
    <span className={styles.metaLabel}>
      {label}
      {tooltip && (
        <Tooltip title={tooltip}>
          <FontAwesomeIcon icon={faCircleInfo} className={styles.infoIcon} />
        </Tooltip>
      )}
    </span>
    {value == null || value === '' ? (
      <span className={styles.metaValueEmpty}>–</span>
    ) : (
      <span className={styles.metaValue}>{value}</span>
    )}
  </div>
);

interface Props {
  data: ItemDetailsResponse | null;
  isMailing: boolean;
  onViewMailingTracking?: () => void;
  onViewShippingTracking?: () => void;
}

const SPECIALS = ['FedEx', 'USPS'];

const formatShippingAddress = (addr: NonNullable<ItemDetailsOverview['shipping']>['shippingAddress'] | undefined): string | null => {
  if (!addr) return null;
  const street = [addr.address1, addr.address2].filter(Boolean).join(', ');
  const cityLine = `${addr.city}, ${addr.state} ${addr.postalCode}`;
  return [street, cityLine].filter(Boolean).join(', ');
};

const formatDistribution = (serviceLevel: string | null | undefined): string | null => {
  if (!serviceLevel) return null;
  return serviceLevel
    .split('_')
    .map(part => {
      const match = SPECIALS.find(s => s.toLowerCase() === part.toLowerCase());
      return match ?? (part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
    })
    .join(' ');
};

const OverviewTab: React.FC<Props> = ({ data, isMailing, onViewMailingTracking, onViewShippingTracking }) => {
  if (!data) return null;

  const { overview, itemDetails } = data;
  const summary = overview.mailTrackingSummary;
  const tracking = overview.tracking;

  const formatMetricValue = (val: number | null | undefined) => {
    if (val == null || val === 0) return null;
    return val.toLocaleString('en-US');
  };

  if (isMailing) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.piecesOrdered}>
          <span className={styles.piecesCount}>{overview.quantity.total?.toLocaleString('en-US') ?? '–'}</span>
          <span className={styles.piecesLabel}>{overview.quantity.total === 1 ? 'Piece' : 'Pieces'} Ordered</span>
          <span className={styles.viewDetailsLink} onClick={onViewMailingTracking}>
            View Mailing Details
          </span>
        </div>

        <StatusBar summary={summary} total={overview.quantity.total} />

        <div className={styles.statusGrid}>
          {STATUS_GRID_ITEMS.map(item => {
            const count = summary[item.key] ?? 0;
            return (
              <div key={item.key} className={styles.statusItem}>
                <div className={styles.statusItemLabel} title={item.label}>{item.label}</div>
                <div className={styles.statusItemBottom}>
                  <div className={styles.statusDot} style={{ background: item.color }} />
                  <span className={styles.statusCount}>{count}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.metricsRow}>
          <div className={styles.metricItem} style={{paddingLeft: '0px'}}>
            <span className={styles.metricLabel}>QR Codes Scanned</span>
            <div className={styles.metricContent}>
              <div className={styles.metricIconWrapper}>
                <FontAwesomeIcon icon={faQrcode} className={`${styles.metricIcon} ${styles.metricIconQr}`} />
              </div>
              {formatMetricValue(tracking.qrCodesScanned) ? (
                <span className={styles.metricValue}>{formatMetricValue(tracking.qrCodesScanned)}</span>
              ) : (
                <span className={styles.metricValueEmpty}>–</span>
              )}
            </div>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Informed Delivery Clicks</span>
            <div className={styles.metricContent}>
              <div className={styles.metricIconWrapper}>
                <FontAwesomeIcon icon={faArrowPointer} className={styles.metricIcon} />
              </div>
              {formatMetricValue(tracking.informedDeliveryClicks) ? (
                <span className={styles.metricValue}>{formatMetricValue(tracking.informedDeliveryClicks)}</span>
              ) : (
                <span className={styles.metricValueEmpty}>–</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.metaSection}>
          <MetaRow label="Item Reference ID" value={overview.itemReferenceId} tooltip="Unique ID your system assigned to this item within your order" noDivider />
          <MetaRow label="Submission Reference ID" value={overview.submissionReferenceId} tooltip="Unique ID your system assigned to the overall order" noDivider />
          <MetaRow label="XD Order ID" value={overview.xdOrderId} tooltip="ID Xpressdocs assigned to your order" noDivider />
          <MetaRow label="Item ID" value={overview.itemId} tooltip="ID our API assigned to this item" />
          <MetaRow label="Delivery Method" value={overview.deliveryMethod} />
          <MetaRow
            label="Date Submitted"
            value={overview.dateSubmitted ? moment(overview.dateSubmitted).format('MM/DD/YYYY hh:mm:ss A') : null}
          />
          <MetaRow
            label="Order Status"
            value={
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                <span className={`${styles.statusBadge} ${getStatusBadgeClass(getFriendlyStatus(overview.orderStatus))}`}>
                  {getFriendlyStatus(overview.orderStatus)}
                </span>
                {overview.orderStatus}
              </span>
            }
          />
          <MetaRow
            label="Mailed Date"
            value={overview.mailedDate ? moment(overview.mailedDate).format('MM/DD/YYYY') : null}
          />
          <MetaRow label="Valid Qty" value={overview.quantity.valid?.toLocaleString('en-US')} />
          <MetaRow label="Invalid Qty" value={overview.quantity.invalid?.toLocaleString('en-US')} noDivider />
        </div>
      </div>
    );
  }

  // Shipped variant
  return (
    <div className={styles.tabContent}>
      <div className={styles.metricsRow} style={{ borderTop: 'none' }}>
        <div className={styles.metricItem} style={{paddingLeft: '0px'}}>
          <span className={styles.metricLabel}>QTY</span>
          <div className={styles.metricContent}>
            <div className={styles.metricIconWrapper}>
              <FontAwesomeIcon icon={faCartShopping} className={styles.metricIcon} />
            </div>
            <span className={styles.metricValue}>{overview.quantity.total?.toLocaleString('en-US') ?? '–'}</span>
          </div>
        </div>
        <div className={styles.metricItem}>
          <span className={styles.metricLabel}>QR Codes Scanned</span>
          <div className={styles.metricContent}>
            <div className={styles.metricIconWrapper}>
              <FontAwesomeIcon icon={faQrcode} className={`${styles.metricIcon} ${styles.metricIconQr}`} />
            </div>
            {formatMetricValue(tracking.qrCodesScanned) ? (
              <span className={styles.metricValue}>{formatMetricValue(tracking.qrCodesScanned)}</span>
            ) : (
              <span className={styles.metricValueEmpty}>–</span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.metaSection}>
        <MetaRow label="Item Reference ID" value={overview.itemReferenceId} tooltip="Unique ID your system assigned to this item within your order" noDivider />
        <MetaRow label="Submission Reference ID" value={overview.submissionReferenceId} tooltip="Unique ID your system assigned to the overall order" noDivider />
        <MetaRow label="XD Order ID" value={overview.xdOrderId} tooltip="ID Xpressdocs assigned to your order" noDivider />
        <MetaRow label="Item ID" value={overview.itemId} tooltip="ID our API assigned to this item" />
        <MetaRow
          label="Shipping Status"
          value={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
              {overview.shipping?.shipments?.[0]?.status ?? '–'}
              <span className={styles.viewDetailsLink} onClick={onViewShippingTracking}>
                View Details
              </span>
            </span>
          }
          noDivider
        />
        <MetaRow label="Shipping Address" value={formatShippingAddress(overview.shipping?.shippingAddress)} />
        <MetaRow label="Distribution" value={formatDistribution(itemDetails.serviceLevel)} />
        <MetaRow
          label="Date Submitted"
          value={overview.dateSubmitted ? moment(overview.dateSubmitted).format('MM/DD/YYYY hh:mm:ss A') : null}
        />
        <MetaRow
          label="Order Status"
          value={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
              <span className={`${styles.statusBadge} ${getStatusBadgeClass(getFriendlyStatus(overview.orderStatus))}`}>
                {getFriendlyStatus(overview.orderStatus)}
              </span>
              {overview.orderStatus}
            </span>
          }
        />
      </div>
    </div>
  );
};

export default OverviewTab;
